/**
 * USER MANAGEMENT FORM
 *
 * Reusable form component for creating and managing team members.
 * Used by both SYSTEM_ADMIN and team admins.
 * Supports filtering users by role and team.
 */

import { useState, useEffect } from 'react';
import { createClient } from '@/lib/supabase';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Trash2,
  Plus,
  Search,
  Edit2,
  AlertCircle,
  Users,
  Loader2,
} from 'lucide-react';
import type { Profile, UserRole } from '@/types/database';
import { ROLE_LABELS, getRoleConfig } from '@/lib/role-colors';

export interface UserManagementFormProps {
  allowedRoles: UserRole[];
  onUserAdded?: () => void;
  onUserDeleted?: () => void;
  onUserRoleChanged?: () => void;
  filterByTeam?: 'PLATFORM' | 'IT' | 'DATA' | null;
  showSearch?: boolean;
  showRoleFilter?: boolean;
  title?: string;
  description?: string;
  currentUserId?: string;
}

interface NewUserForm {
  email: string;
  fullName: string;
  password: string;
  role: UserRole;
}

interface EditingUser {
  id: string;
  role: UserRole;
}

export function UserManagementForm({
  allowedRoles,
  onUserAdded,
  onUserDeleted,
  onUserRoleChanged,
  filterByTeam,
  showSearch = true,
  showRoleFilter = true,
  title = 'Team Members',
  description = 'Create and manage team members',
  currentUserId,
}: UserManagementFormProps) {
  const [allUsers, setAllUsers] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRole, setSelectedRole] = useState<UserRole | 'all'>('all');
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState<NewUserForm>({
    email: '',
    fullName: '',
    password: '',
    role: allowedRoles[0] || 'PLATFORM_TEAM',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [editingUser, setEditingUser] = useState<EditingUser | null>(null);
  const [updatingUserId, setUpdatingUserId] = useState<string | null>(null);
  const [deletingUserId, setDeletingUserId] = useState<string | null>(null);

  // Fetch users on component mount
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        setError(null);
        const supabase = createClient();

        // Fetch users based on filter
        let query = supabase.from('profiles').select('*');

        // If filtering by team roles, only show those roles
        if (filterByTeam) {
          const teamRoles = getAllowedRolesForTeam(filterByTeam);
          query = query.in('role', teamRoles as any[]);
        }

        const { data: usersData, error: usersError } = await query.order('created_at', { ascending: false });

        if (usersError) throw usersError;
        setAllUsers((usersData as Profile[]) || []);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to fetch users'));
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [filterByTeam]);

  // Get all allowed roles for a specific team
  const getAllowedRolesForTeam = (team: 'PLATFORM' | 'IT' | 'DATA'): UserRole[] => {
    switch (team) {
      case 'PLATFORM':
        return ['PLATFORM_TEAM', 'PLATFORM_ADMIN'];
      case 'IT':
        return ['IT_TEAM', 'IT_ADMIN'];
      case 'DATA':
        return ['DATA_TEAM', 'DATA_ADMIN'];
    }
  };

  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (!formData.email || !formData.fullName || !formData.password) {
      setFormError('Please fill in all required fields');
      return;
    }

    if (formData.password.length < 6) {
      setFormError('Password must be at least 6 characters');
      return;
    }

    if (!allowedRoles.includes(formData.role)) {
      setFormError('You cannot assign this role');
      return;
    }

    setIsSubmitting(true);

    try {
      const supabase = createClient();
      const { data: { session } } = await supabase.auth.getSession();

      if (!session?.access_token) {
        setFormError('No active session');
        return;
      }

      // Call the edge function to create the user
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const response = await fetch(`${supabaseUrl}/functions/v1/create-user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
          fullName: formData.fullName,
          role: formData.role,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to create user');
      }

      setShowAddForm(false);
      setFormData({ email: '', fullName: '', password: '', role: allowedRoles[0] || 'PLATFORM_TEAM' });

      // Refresh users list with same filtering as initial load
      let query = supabase.from('profiles').select('*');
      if (filterByTeam) {
        const teamRoles = getAllowedRolesForTeam(filterByTeam);
        query = query.in('role', teamRoles as any[]);
      }
      const { data: usersData } = await query.order('created_at', { ascending: false });
      setAllUsers((usersData as Profile[]) || []);

      onUserAdded?.();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to create user');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateRole = async (userId: string, newRole: UserRole) => {
    try {
      setUpdatingUserId(userId);
      const supabase = createClient();
      const { data: { session } } = await supabase.auth.getSession();

      if (!session?.access_token) {
        setError(new Error('No active session'));
        return;
      }

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const response = await fetch(`${supabaseUrl}/functions/v1/update-user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          userId,
          role: newRole,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to update user role');
      }

      setEditingUser(null);

      // Refresh users list with same filtering as initial load
      let query = supabase.from('profiles').select('*');
      if (filterByTeam) {
        const teamRoles = getAllowedRolesForTeam(filterByTeam);
        query = query.in('role', teamRoles as any[]);
      }
      const { data: usersData } = await query.order('created_at', { ascending: false });
      setAllUsers((usersData as Profile[]) || []);

      onUserRoleChanged?.();
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to update user role'));
    } finally {
      setUpdatingUserId(null);
    }
  };

  const handleDeleteUser = async (userId: string, userEmail: string) => {
    if (!confirm(`Are you sure you want to delete ${userEmail}?`)) {
      return;
    }

    try {
      setDeletingUserId(userId);
      const supabase = createClient();
      const { data: { session } } = await supabase.auth.getSession();

      if (!session?.access_token) {
        setError(new Error('No active session'));
        return;
      }

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const response = await fetch(`${supabaseUrl}/functions/v1/delete-user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          userId,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to delete user');
      }

      // Refresh users list with same filtering as initial load
      let query = supabase.from('profiles').select('*');
      if (filterByTeam) {
        const teamRoles = getAllowedRolesForTeam(filterByTeam);
        query = query.in('role', teamRoles as any[]);
      }
      const { data: usersData } = await query.order('created_at', { ascending: false });
      setAllUsers((usersData as Profile[]) || []);

      onUserDeleted?.();
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to delete user'));
    } finally {
      setDeletingUserId(null);
    }
  };

  if (!allowedRoles || allowedRoles.length === 0) {
    return (
      <div className='flex items-center justify-center min-h-screen'>
        <Card className='max-w-md'>
          <CardContent className='flex flex-col items-center justify-center py-12'>
            <AlertCircle className='h-12 w-12 text-red-600 dark:text-red-400 mb-4' />
            <h3 className='text-lg font-semibold mb-2'>Configuration Error</h3>
            <p className='text-muted-foreground text-center'>
              No roles configured for user management. Please contact your administrator.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className='flex items-center justify-center min-h-screen'>
        <div className='text-center'>
          <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 dark:border-gray-100 mx-auto'></div>
          <p className='mt-4 text-gray-600 dark:text-gray-400'>Loading...</p>
        </div>
      </div>
    );
  }

  // Filter users
  let filteredUsers = allUsers;
  if (selectedRole !== 'all') {
    filteredUsers = filteredUsers.filter((u) => u.role === selectedRole);
  }
  if (searchQuery) {
    filteredUsers = filteredUsers.filter(
      (u) =>
        u.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
        u.full_name?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }

  // Group users by role
  const groupedUsers: Record<UserRole, Profile[]> = {
    SYSTEM_ADMIN: [],
    PLATFORM_TEAM: [],
    PLATFORM_ADMIN: [],
    IT_TEAM: [],
    IT_ADMIN: [],
    DATA_TEAM: [],
    DATA_ADMIN: [],
  };

  filteredUsers.forEach((u) => {
    groupedUsers[u.role as UserRole].push(u);
  });

  const rolesWithUsers = (
    selectedRole === 'all'
      ? (Object.keys(groupedUsers) as UserRole[]).filter((r) => groupedUsers[r].length > 0)
      : [selectedRole as UserRole]
  );

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div className='flex items-start justify-between'>
        <div>
          <h1 className='text-3xl font-bold tracking-tight'>{title}</h1>
          <p className='text-sm text-muted-foreground mt-2'>{description}</p>
        </div>
        <Button
          onClick={() => setShowAddForm(!showAddForm)}
          className='gap-2 whitespace-nowrap'
          disabled={isSubmitting || !!updatingUserId || !!deletingUserId}
        >
          <Plus className='h-4 w-4' />
          Add Member
        </Button>
      </div>

      {/* Error Message */}
      {error && (
        <Card className='border-red-200 dark:border-red-800'>
          <CardContent className='pt-6'>
            <div className='flex items-center gap-2 text-sm text-red-600 dark:text-red-400'>
              <AlertCircle className='h-4 w-4 flex-shrink-0' />
              {error.message}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Add User Dialog */}
      <Dialog open={showAddForm} onOpenChange={setShowAddForm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Team Member</DialogTitle>
            <DialogDescription>Add a new member to your team</DialogDescription>
          </DialogHeader>

          <form onSubmit={handleAddUser} className='space-y-4'>
            <div className='grid grid-cols-3 gap-4'>
              <div className='col-span-2 space-y-2'>
                <label className='text-sm font-medium'>Full Name</label>
                <Input
                  type='text'
                  placeholder='John Doe'
                  value={formData.fullName}
                  onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                  disabled={isSubmitting}
                  required
                />
              </div>
              <div className='space-y-2'>
                <label className='text-sm font-medium'>Role</label>
                <Select
                  value={formData.role}
                  onValueChange={(value) => setFormData({ ...formData, role: value as UserRole })}
                  disabled={isSubmitting}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {allowedRoles.map((role) => (
                      <SelectItem key={role} value={role}>
                        {ROLE_LABELS[role]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className='space-y-2'>
              <label className='text-sm font-medium'>Email</label>
              <Input
                type='email'
                placeholder='user@example.com'
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                disabled={isSubmitting}
                required
              />
            </div>

            <div className='space-y-2'>
              <label className='text-sm font-medium'>Password</label>
              <Input
                type='password'
                placeholder='At least 6 characters'
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                disabled={isSubmitting}
                required
              />
            </div>

            {formError && (
              <div className='flex items-center gap-2 text-sm text-destructive bg-destructive/10 p-3 rounded-md'>
                <AlertCircle className='h-4 w-4 flex-shrink-0' />
                {formError}
              </div>
            )}

            <div className='flex gap-2 justify-end pt-4'>
              <Button
                type='button'
                variant='outline'
                onClick={() => {
                  setShowAddForm(false);
                  setFormError(null);
                }}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button type='submit' disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                    Creating...
                  </>
                ) : (
                  'Create'
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Filters */}
      <div className='flex gap-4 items-center'>
        {showSearch && (
          <div className='flex-1 relative'>
            <Search className='absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground' />
            <Input
              placeholder='Search by name or email...'
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className='pl-10'
            />
          </div>
        )}
        {showRoleFilter && (
          <Select
            value={selectedRole}
            onValueChange={(value) => setSelectedRole(value as UserRole | 'all')}
          >
            <SelectTrigger className='w-48'>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='all'>All Roles</SelectItem>
              {allowedRoles.map((role) => (
                <SelectItem key={role} value={role}>
                  {ROLE_LABELS[role]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Users List by Role */}
      <div className='space-y-6'>
        {rolesWithUsers.length > 0 ? (
          rolesWithUsers.map((role) => (
            <Card key={role}>
              <CardHeader>
                <CardTitle className='flex items-center gap-2'>
                  {ROLE_LABELS[role]}
                  <Badge variant='outline'>{groupedUsers[role].length}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className='space-y-3'>
                  {groupedUsers[role].map((user) => (
                    <div key={user.id} className='flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-900/30 rounded-lg'>
                      <div className='flex-1 min-w-0'>
                        <p className='font-medium text-sm truncate'>{user.full_name}</p>
                        <p className='text-xs text-muted-foreground truncate'>{user.email}</p>
                      </div>

                      {editingUser?.id === user.id ? (
                        <div className='flex items-center gap-2'>
                          <Select
                            value={editingUser.role}
                            onValueChange={(value) =>
                              setEditingUser({ ...editingUser, role: value as UserRole })
                            }
                            disabled={updatingUserId === user.id}
                          >
                            <SelectTrigger className='w-32 h-9'>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {allowedRoles.map((role) => (
                                <SelectItem key={role} value={role}>
                                  {ROLE_LABELS[role]}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Button
                            size='sm'
                            onClick={() =>
                              handleUpdateRole(user.id, editingUser.role)
                            }
                            disabled={updatingUserId === user.id}
                          >
                            {updatingUserId === user.id ? (
                              <>
                                <Loader2 className='h-3 w-3 mr-1 animate-spin' />
                                Saving...
                              </>
                            ) : (
                              'Save'
                            )}
                          </Button>
                          <Button
                            size='sm'
                            variant='outline'
                            onClick={() => setEditingUser(null)}
                            disabled={updatingUserId === user.id}
                          >
                            Cancel
                          </Button>
                        </div>
                      ) : currentUserId === user.id ? (
                        <Badge className={getRoleConfig(user.role)?.badgeClass}>
                          {getRoleConfig(user.role)?.label} • Current
                        </Badge>
                      ) : (
                        <div className='flex items-center gap-2'>
                          <Badge className={getRoleConfig(user.role)?.badgeClass}>
                            {getRoleConfig(user.role)?.label}
                          </Badge>
                          <Button
                            size='sm'
                            variant='ghost'
                            onClick={() => setEditingUser({ id: user.id, role: user.role })}
                            disabled={isSubmitting || updatingUserId !== null || deletingUserId !== null}
                          >
                            <Edit2 className='h-4 w-4' />
                          </Button>
                          <Button
                            size='sm'
                            variant='ghost'
                            onClick={() => handleDeleteUser(user.id, user.email)}
                            disabled={deletingUserId === user.id || isSubmitting || updatingUserId !== null}
                          >
                            {deletingUserId === user.id ? (
                              <Loader2 className='h-4 w-4 animate-spin' />
                            ) : (
                              <Trash2 className='h-4 w-4' />
                            )}
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))
        ) : (
          <Card>
            <CardContent className='flex flex-col items-center justify-center py-12'>
              <Users className='h-12 w-12 text-muted-foreground mb-4' />
              <h3 className='text-lg font-semibold mb-2'>No Team Members</h3>
              <p className='text-muted-foreground text-center max-w-md'>
                {allUsers.length === 0
                  ? 'No team members found. Click "Add Member" to get started.'
                  : 'No team members found matching your filters.'}
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
